import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Navigate, useNavigate } from 'react-router-dom';
import api from '../utils/api';

interface Customer {
  id: string;
  email: string;
  name: string;
  username: string;
  role: string;
  companyName?: string;
  stripeCustomerId?: string;
  subscriptionId?: string;
  subscriptionStatus?: string;
  planName?: string;
  subscriptionPrice?: number;
  subscriptionBillingStart?: string;
  stripeSyncStatus?: string; // 'synced' | 'action_required' | 'error'
  stripeSyncMessage?: string;
  stripeSyncAt?: string;
  isEmailVerified?: boolean;
  propertyGroup?: {
    id: string;
    name: string;
    propertyCount: number;
  };
  subscription?: {
    id: string;
    planName: string;
    price: number;
    status: string;
    stripeSubscriptionId?: string;
  };
  createdAt: string;
  updatedAt: string;
  lastActive?: string;
}

interface SubscriptionPlan {
  id: string;
  name: string;
  basePrice: number;
  description: string;
}

const CustomerManagement: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [filteredCustomers, setFilteredCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [showPricingModal, setShowPricingModal] = useState(false);
  const [showCustomerDetails, setShowCustomerDetails] = useState(false);
  const [showActionsMenu, setShowActionsMenu] = useState<string | null>(null);
  const [availablePlans, setAvailablePlans] = useState<SubscriptionPlan[]>([]);
  const [customPrice, setCustomPrice] = useState<number>(0);
  const [selectedPlan, setSelectedPlan] = useState<string>('');
  const [authLoading, setAuthLoading] = useState(true);
  
  // Search and filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [planFilter, setPlanFilter] = useState('all');
  
  // Admin action states
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [actionMessage, setActionMessage] = useState('');

  // Billing date modal state
  const [showBillingDateModal, setShowBillingDateModal] = useState(false);
  const [billingDateCustomerId, setBillingDateCustomerId] = useState<string | null>(null);
  const [billingDateInput, setBillingDateInput] = useState('');

  useEffect(() => {
    // Check if authentication has loaded
    const checkAuth = () => {
      const storedUser = localStorage.getItem('user');
      const storedToken = localStorage.getItem('token');
      
      if (storedUser || storedToken || user) {
        setAuthLoading(false);
      } else {
        // If no stored data and no user, wait a bit longer
        setTimeout(() => setAuthLoading(false), 1000);
      }
    };

    checkAuth();
  }, [user]);

  const fetchCustomers = async () => {
    try {
      setLoading(true);
      const response = await api.get<Customer[]>('/api/platform-admin/customers');
      setCustomers(response.data);
      setFilteredCustomers(response.data);
      setError('');
    } catch (err: any) {
      console.error('Error fetching customers:', err);
      setError('Failed to load customers');
    } finally {
      setLoading(false);
    }
  };

  const fetchSubscriptionPlans = async () => {
    try {
      const response = await api.get<SubscriptionPlan[]>('/api/platform-admin/subscription-plans');
      setAvailablePlans(response.data);
    } catch (err: any) {
      console.error('Error fetching subscription plans:', err);
    }
  };

  useEffect(() => {
    if (user && user.role === 'PLATFORM_ADMIN') {
      fetchCustomers();
      fetchSubscriptionPlans();
    }
  }, [user]);

  // Filter customers based on search and filters
  useEffect(() => {
    let filtered = customers;

    // Search filter
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(customer => 
        customer.email.toLowerCase().includes(term) ||
        customer.name?.toLowerCase().includes(term) ||
        customer.companyName?.toLowerCase().includes(term) ||
        customer.stripeCustomerId?.toLowerCase().includes(term) ||
        customer.id.toLowerCase().includes(term)
      );
    }

    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(customer => {
        if (statusFilter === 'active') return customer.subscriptionStatus === 'active';
        if (statusFilter === 'trialing') return customer.subscriptionStatus === 'trialing';
        if (statusFilter === 'cancelled') return customer.subscriptionStatus === 'cancelled';
        if (statusFilter === 'past_due') return customer.subscriptionStatus === 'past_due';
        return true;
      });
    }

    // Plan filter
    if (planFilter !== 'all') {
      filtered = filtered.filter(customer => customer.planName?.toLowerCase() === planFilter.toLowerCase());
    }

    setFilteredCustomers(filtered);
  }, [customers, searchTerm, statusFilter, planFilter]);

  // Show loading while authentication is initializing
  if (authLoading) {
    return (
      <div className="min-h-screen bg-gray-900 text-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-500 mx-auto"></div>
          <p className="mt-4 text-gray-400">Loading...</p>
        </div>
      </div>
    );
  }

  // Redirect if not platform admin
  if (!user || user.role !== 'PLATFORM_ADMIN') {
    return <Navigate to="/admin/login" replace />;
  }

  const openPricingModal = (customer: Customer) => {
    setSelectedCustomer(customer);
    setSelectedPlan(customer.subscription?.planName || '');
    setCustomPrice(customer.subscription?.price || 0);
    setShowPricingModal(true);
    setShowActionsMenu(null);
  };

  const closePricingModal = () => {
    setSelectedCustomer(null);
    setShowPricingModal(false);
    setSelectedPlan('');
    setCustomPrice(0);
  };

  const openCustomerDetails = (customer: Customer) => {
    setSelectedCustomer(customer);
    setShowCustomerDetails(true);
    setShowActionsMenu(null);
  };

  const closeCustomerDetails = () => {
    setSelectedCustomer(null);
    setShowCustomerDetails(false);
  };

  // Admin action functions
  const resetPassword = async (customerId: string) => {
    try {
      setActionLoading('reset-password');
      const response = await api.post<{message: string, instructions: string, temporaryPassword: string, customerEmail: string}>(`/api/platform-admin/customers/${customerId}/reset-password`);
      setActionMessage(`${response.data.message}. Temporary password: ${response.data.temporaryPassword}. ${response.data.instructions}`);
      setTimeout(() => setActionMessage(''), 10000);
      setShowActionsMenu(null);
    } catch (err: any) {
      console.error('Error resetting password:', err);
      setActionMessage('Failed to reset password');
      setTimeout(() => setActionMessage(''), 3000);
    } finally {
      setActionLoading(null);
    }
  };

  const applyCredit = async (customerId: string, amount: number, reason?: string) => {
    try {
      setActionLoading('apply-credit');
      await api.post(`/api/platform-admin/customers/${customerId}/apply-credit`, { amount, reason });
      setActionMessage(`$${amount.toFixed(2)} credit applied successfully`);
      setTimeout(() => setActionMessage(''), 3000);
      fetchCustomers(); // Refresh data
      setShowActionsMenu(null);
    } catch (err: any) {
      console.error('Error applying credit:', err);
      setActionMessage('Failed to apply credit');
      setTimeout(() => setActionMessage(''), 3000);
    } finally {
      setActionLoading(null);
    }
  };

  const pauseSubscription = async (customerId: string, reason?: string) => {
    try {
      setActionLoading('pause-subscription');
      await api.post(`/api/platform-admin/customers/${customerId}/pause-subscription`, { reason });
      setActionMessage('Subscription paused successfully');
      setTimeout(() => setActionMessage(''), 3000);
      fetchCustomers(); // Refresh data
      setShowActionsMenu(null);
    } catch (err: any) {
      console.error('Error pausing subscription:', err);
      setActionMessage('Failed to pause subscription');
      setTimeout(() => setActionMessage(''), 3000);
    } finally {
      setActionLoading(null);
    }
  };

  const resumeSubscription = async (customerId: string) => {
    try {
      setActionLoading('resume-subscription');
      await api.post(`/api/platform-admin/customers/${customerId}/resume-subscription`);
      setActionMessage('Subscription resumed successfully');
      setTimeout(() => setActionMessage(''), 3000);
      fetchCustomers(); // Refresh data
      setShowActionsMenu(null);
    } catch (err: any) {
      console.error('Error resuming subscription:', err);
      setActionMessage('Failed to resume subscription');
      setTimeout(() => setActionMessage(''), 3000);
    } finally {
      setActionLoading(null);
    }
  };

  const deleteCustomer = async (customerId: string, customerEmail: string) => {
    const confirmed = window.confirm(
      `Are you sure you want to delete customer ${customerEmail}?\n\n` +
      `This will:\n` +
      `- Cancel their Stripe subscription\n` +
      `- Delete their empty property groups\n` +
      `- Permanently remove their account\n\n` +
      `This action cannot be undone!`
    );

    if (!confirmed) return;

    try {
      setActionLoading('delete-customer');
      await api.delete(`/api/platform-admin/customers/${customerId}`);
      setActionMessage(`Customer ${customerEmail} deleted successfully`);
      setTimeout(() => setActionMessage(''), 5000);
      fetchCustomers(); // Refresh data
      setShowActionsMenu(null);
    } catch (err: any) {
      console.error('Error deleting customer:', err);
      const errorMessage = err.response?.data?.message || 'Failed to delete customer';
      setActionMessage(`Error: ${errorMessage}`);
      setTimeout(() => setActionMessage(''), 5000);
      
      // Show detailed error if customer has property data
      if (err.response?.data?.propertyGroups) {
        const groups = err.response.data.propertyGroups;
        alert(
          `Cannot delete customer with active property groups:\n\n` +
          groups.map((g: any) => 
            `- ${g.name}: ${g.addressCount} addresses, ${g.memberCount} members`
          ).join('\n') +
          `\n\nPlease remove all property data before deleting the customer.`
        );
      }
    } finally {
      setActionLoading(null);
    }
  };

  const needsStripeSyncAttention = (customer: Customer) =>
    customer.stripeSyncStatus === 'error' || customer.stripeSyncStatus === 'action_required';

  const retryStripeSync = async (customerId: string) => {
    try {
      setActionLoading('retry-stripe-sync');
      const response = await api.post<{ message: string }>(`/api/platform-admin/customers/${customerId}/retry-stripe-sync`);
      setActionMessage(response.data.message);
      setTimeout(() => setActionMessage(''), 6000);
      fetchCustomers(); // Refresh so the badge reflects the new sync status
      setShowActionsMenu(null);
    } catch (err: any) {
      console.error('Error retrying Stripe sync:', err);
      setActionMessage('Error: ' + (err.response?.data?.message || 'Failed to retry Stripe sync'));
      setTimeout(() => setActionMessage(''), 5000);
    } finally {
      setActionLoading(null);
    }
  };

  const resolveStripeSync = async (customerId: string) => {
    const note = prompt(
      'Mark billing as resolved?\n\nThis clears the Stripe sync warning. Only do this after confirming the Stripe subscription matches.\n\nOptional note for the audit log:'
    );
    if (note === null) return; // Cancelled
    try {
      setActionLoading('resolve-stripe-sync');
      const response = await api.post<{ message: string }>(
        `/api/platform-admin/customers/${customerId}/resolve-stripe-sync`,
        { note: note || undefined }
      );
      setActionMessage(response.data.message);
      setTimeout(() => setActionMessage(''), 6000);
      fetchCustomers(); // Refresh so the badge clears
      setShowActionsMenu(null);
    } catch (err: any) {
      console.error('Error resolving Stripe sync:', err);
      setActionMessage('Error: ' + (err.response?.data?.message || 'Failed to mark billing as resolved'));
      setTimeout(() => setActionMessage(''), 5000);
    } finally {
      setActionLoading(null);
    }
  };

  const openBillingDateModal = (customer: Customer) => {
    setBillingDateCustomerId(customer.id);
    const existingDate = customer.subscriptionBillingStart
      ? new Date(customer.subscriptionBillingStart).toISOString().split('T')[0]
      : new Date().toISOString().split('T')[0];
    setBillingDateInput(existingDate);
    setShowBillingDateModal(true);
    setShowActionsMenu(null);
  };

  const closeBillingDateModal = () => {
    setShowBillingDateModal(false);
    setBillingDateCustomerId(null);
    setBillingDateInput('');
  };

  const saveBillingDate = async () => {
    if (!billingDateCustomerId || !billingDateInput) return;
    try {
      setActionLoading('set-billing-date');
      const response = await api.post<{ message: string; billingStartDate: string; stripeUpdated: boolean }>(
        `/api/platform-admin/customers/${billingDateCustomerId}/set-billing-date`,
        { billingStartDate: billingDateInput }
      );
      setActionMessage(response.data.message);
      setTimeout(() => setActionMessage(''), 6000);
      setCustomers(prev => prev.map(c =>
        c.id === billingDateCustomerId
          ? { ...c, subscriptionBillingStart: response.data.billingStartDate }
          : c
      ));
      closeBillingDateModal();
    } catch (err: any) {
      setActionMessage('Error: ' + (err.response?.data?.message || 'Failed to set billing date'));
      setTimeout(() => setActionMessage(''), 5000);
    } finally {
      setActionLoading(null);
    }
  };

  const updateCustomerSubscription = async () => {
    if (!selectedCustomer) return;

    try {
      const response = await api.put<{ subscription: Customer['subscription']; stripeSyncStatus?: string }>(`/api/platform-admin/customers/${selectedCustomer.id}/subscription`, {
        planName: selectedPlan,
        customPrice: customPrice >= 0 ? customPrice : undefined, // Allow 0 for free
      });

      closePricingModal();
      // Refresh from the server so the persisted Stripe sync status/badge is shown
      await fetchCustomers();
      const syncNote = response.data.stripeSyncStatus ? `\n\nStripe sync: ${response.data.stripeSyncStatus}` : '';
      alert('Customer subscription updated successfully!' + syncNote);
    } catch (err: any) {
      console.error('Error updating subscription:', err);
      alert('Failed to update subscription: ' + (err.response?.data?.message || 'Unknown error'));
    }
  };

  const stripeSyncBadge = (customer: Customer) => {
    if (customer.stripeSyncStatus !== 'error' && customer.stripeSyncStatus !== 'action_required') return null;
    const isError = customer.stripeSyncStatus === 'error';
    return (
      <span
        title={customer.stripeSyncMessage || 'Stripe subscription may be out of sync'}
        className={`inline-flex items-center px-2 py-0.5 text-xs font-semibold rounded-full whitespace-nowrap border ${
          isError
            ? 'text-red-400 bg-red-900/20 border-red-500/40'
            : 'text-yellow-400 bg-yellow-900/20 border-yellow-500/40'
        }`}
      >
        ⚠ {isError ? 'Stripe sync failed' : 'Stripe out of sync'}
      </span>
    );
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount / 100);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString();
  };


  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 text-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-500 mx-auto"></div>
          <p className="mt-4 text-gray-400">Loading customers...</p>
        </div>
      </div>
    );
  }

  const getStatusColor = (status?: string) => {
    switch (status) {
      case 'active': return 'text-green-400 bg-green-900/20';
      case 'trialing': return 'text-blue-400 bg-blue-900/20';
      case 'cancelled': return 'text-red-400 bg-red-900/20';
      case 'past_due': return 'text-yellow-400 bg-yellow-900/20';
      default: return 'text-gray-400 bg-gray-900/20';
    }
  };

  if (error) {
    return (
      <div className="min-h-screen bg-gray-900 text-white flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-400 mb-4">{error}</p>
          <button 
            onClick={fetchCustomers}
            className="bg-blue-600 hover:bg-blue-700 px-4 py-2 rounded-lg"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 text-white p-4 lg:p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <button
            onClick={() => navigate('/admin/dashboard')}
            className="flex items-center text-gray-400 hover:text-white mb-4 transition-colors"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to Dashboard
          </button>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl lg:text-3xl font-bold text-white mb-2">Customer Management</h1>
              <p className="text-sm lg:text-base text-gray-400">Manage customer subscriptions and pricing</p>
            </div>
            <a
              href="/profile"
              className="bg-gray-700 hover:bg-gray-600 px-4 py-2 rounded-lg text-sm font-medium transition duration-200 text-white flex-shrink-0"
            >
              My Profile
            </a>
          </div>
        </div>

        {/* Search and Filter Controls */}
        <div className="bg-gray-800 rounded-lg p-4 lg:p-6 mb-6">
          <div className="space-y-3 lg:space-y-0 lg:grid lg:grid-cols-3 lg:gap-4">
            {/* Search Bar - Full width on mobile */}
            <div className="lg:col-span-3">
              <input
                type="text"
                placeholder="Search by email, name, company, or ID..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Status Filter */}
            <div>
              <label className="block text-xs font-medium text-gray-400 mb-1">
                Subscription Status
              </label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All Statuses</option>
                <option value="active">Active</option>
                <option value="trialing">Trialing</option>
                <option value="cancelled">Cancelled</option>
                <option value="past_due">Past Due</option>
              </select>
            </div>

            {/* Plan Filter */}
            <div>
              <label className="block text-xs font-medium text-gray-400 mb-1">
                Plan
              </label>
              <select
                value={planFilter}
                onChange={(e) => setPlanFilter(e.target.value)}
                className="w-full px-4 py-2 bg-gray-700 border border-gray-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="all">All Plans</option>
                <option value="starter">Starter</option>
                <option value="growth">Growth</option>
                <option value="professional">Professional</option>
                <option value="enterprise">Enterprise</option>
              </select>
            </div>

            {/* Results Summary */}
            <div className="flex items-end">
              <div className="text-xs lg:text-sm text-gray-400">
                Showing {filteredCustomers.length} of {customers.length} customers
              </div>
            </div>
          </div>
        </div>

        {/* Action Messages */}
        {actionMessage && (
          <div className="mb-6 p-4 rounded-lg bg-blue-900/20 border border-blue-500/20">
            <p className="text-sm lg:text-base text-blue-400">{actionMessage}</p>
          </div>
        )}

        {/* Mobile Card View */}
        <div className="lg:hidden space-y-4">
          {filteredCustomers.map((customer) => (
            <div key={customer.id} className="bg-gray-800 rounded-lg p-4 relative">
              {/* Customer Header */}
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1 min-w-0">
                  <h3 className="text-base font-semibold text-white truncate">{customer.username}</h3>
                  <p className="text-sm text-gray-400 truncate">{customer.email}</p>
                  <div className="mt-1">{stripeSyncBadge(customer)}</div>
                </div>
                {customer.subscription && (
                  <span className={`ml-2 inline-flex px-2 py-1 text-xs font-semibold rounded-full whitespace-nowrap ${getStatusColor(customer.subscription.status)}`}>
                    {customer.subscription.status}
                  </span>
                )}
              </div>

              {/* Customer Details */}
              <div className="grid grid-cols-2 gap-3 mb-4">
                <div>
                  <p className="text-xs text-gray-500 mb-1">Property Group</p>
                  {customer.propertyGroup ? (
                    <div>
                      <p className="text-sm text-white font-medium">{customer.propertyGroup.name}</p>
                      <p className="text-xs text-gray-400">{customer.propertyGroup.propertyCount} properties</p>
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500">No group</p>
                  )}
                </div>
                <div>
                  <p className="text-xs text-gray-500 mb-1">Subscription</p>
                  {customer.subscription ? (
                    <div>
                      <p className="text-sm text-white font-medium">{customer.subscription.planName}</p>
                      <p className={`text-sm font-semibold ${customer.subscription.price === 0 ? 'text-blue-400' : 'text-green-400'}`}>
                        {customer.subscription.price === 0 ? 'No Charge' : formatCurrency(customer.subscription.price)}
                      </p>
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500">No subscription</p>
                  )}
                </div>
              </div>

              {/* Quick Actions */}
              <div className="flex gap-2">
                <button
                  onClick={() => openCustomerDetails(customer)}
                  className="flex-1 text-sm py-2 px-3 bg-blue-900/30 text-blue-400 rounded-lg hover:bg-blue-900/50 transition-colors"
                >
                  View Details
                </button>
                <button
                  onClick={() => setShowActionsMenu(showActionsMenu === customer.id ? null : customer.id)}
                  className="px-4 py-2 bg-gray-700 text-gray-300 rounded-lg hover:bg-gray-600 transition-colors"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
                  </svg>
                </button>
              </div>

              {/* Actions Dropdown Menu */}
              {showActionsMenu === customer.id && (
                <div className="absolute right-4 top-full mt-2 w-56 bg-gray-700 rounded-lg shadow-xl z-10 py-2">
                  <button
                    onClick={() => openPricingModal(customer)}
                    className="w-full text-left px-4 py-2 text-sm text-blue-400 hover:bg-gray-600"
                  >
                    Edit Pricing
                  </button>
                  <button
                    onClick={() => openBillingDateModal(customer)}
                    className="w-full text-left px-4 py-2 text-sm text-orange-400 hover:bg-gray-600"
                  >
                    Set Billing Date
                  </button>
                  {needsStripeSyncAttention(customer) && (
                    <>
                      <button
                        onClick={() => retryStripeSync(customer.id)}
                        disabled={actionLoading === 'retry-stripe-sync'}
                        className="w-full text-left px-4 py-2 text-sm text-purple-400 hover:bg-gray-600 disabled:opacity-50"
                      >
                        {actionLoading === 'retry-stripe-sync' ? 'Retrying...' : 'Retry Stripe Sync'}
                      </button>
                      <button
                        onClick={() => resolveStripeSync(customer.id)}
                        disabled={actionLoading === 'resolve-stripe-sync'}
                        className="w-full text-left px-4 py-2 text-sm text-teal-400 hover:bg-gray-600 disabled:opacity-50"
                      >
                        {actionLoading === 'resolve-stripe-sync' ? 'Resolving...' : 'Mark Billing Resolved'}
                      </button>
                    </>
                  )}
                  <button
                    onClick={() => resetPassword(customer.id)}
                    disabled={actionLoading === 'reset-password'}
                    className="w-full text-left px-4 py-2 text-sm text-yellow-400 hover:bg-gray-600 disabled:opacity-50"
                  >
                    {actionLoading === 'reset-password' ? 'Resetting...' : 'Reset Password'}
                  </button>
                  <button
                    onClick={() => {
                      const amount = prompt('Enter credit amount (USD):');
                      const reason = prompt('Enter reason for credit:');
                      if (amount && !isNaN(Number(amount))) {
                        applyCredit(customer.id, Number(amount), reason || 'Admin credit');
                      }
                    }}
                    disabled={actionLoading === 'apply-credit'}
                    className="w-full text-left px-4 py-2 text-sm text-cyan-300 hover:bg-gray-600 disabled:opacity-50"
                  >
                    {actionLoading === 'apply-credit' ? 'Applying...' : 'Apply Credit'}
                  </button>
                  {customer.subscriptionStatus === 'active' ? (
                    <button
                      onClick={() => {
                        const reason = prompt('Enter reason for pausing subscription:');
                        pauseSubscription(customer.id, reason || 'Admin action');
                      }}
                      disabled={actionLoading === 'pause-subscription'}
                      className="w-full text-left px-4 py-2 text-sm text-red-400 hover:bg-gray-600 disabled:opacity-50"
                    >
                      {actionLoading === 'pause-subscription' ? 'Pausing...' : 'Pause Subscription'}
                    </button>
                  ) : (
                    <button
                      onClick={() => resumeSubscription(customer.id)}
                      disabled={actionLoading === 'resume-subscription'}
                      className="w-full text-left px-4 py-2 text-sm text-green-400 hover:bg-gray-600 disabled:opacity-50"
                    >
                      {actionLoading === 'resume-subscription' ? 'Resuming...' : 'Resume Subscription'}
                    </button>
                  )}
                  <hr className="my-1 border-gray-600" />
                  <button
                    onClick={() => deleteCustomer(customer.id, customer.email)}
                    disabled={actionLoading === 'delete-customer'}
                    className="w-full text-left px-4 py-2 text-sm text-red-500 hover:bg-red-900/20 disabled:opacity-50 font-semibold"
                  >
                    {actionLoading === 'delete-customer' ? 'Deleting...' : 'Delete Customer'}
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Desktop Table View */}
        <div className="hidden lg:block bg-gray-800 rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-700">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                    Customer
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                    Property Group
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                    Subscription
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                    Monthly Price
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                    Billing Start
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                    Joined
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700">
                {filteredCustomers.map((customer) => (
                  <tr key={customer.id} className="hover:bg-gray-700">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-white">{customer.username}</div>
                        <div className="text-sm text-gray-400">{customer.email}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {customer.propertyGroup ? (
                        <div>
                          <div className="text-sm text-white">{customer.propertyGroup.name}</div>
                          <div className="text-sm text-gray-400">{customer.propertyGroup.propertyCount} properties</div>
                        </div>
                      ) : (
                        <span className="text-gray-500">No property group</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {customer.subscription ? (
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-white">{customer.subscription.planName}</span>
                          {stripeSyncBadge(customer)}
                        </div>
                      ) : (
                        <span className="text-gray-500">No subscription</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {customer.subscription ? (
                        <span className={`text-sm font-medium ${customer.subscription.price === 0 ? 'text-blue-400' : 'text-green-400'}`}>
                          {customer.subscription.price === 0 ? 'No Charge' : formatCurrency(customer.subscription.price)}
                        </span>
                      ) : (
                        <span className="text-gray-500">-</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {customer.subscription ? (
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(customer.subscription.status)}`}>
                          {customer.subscription.status}
                        </span>
                      ) : (
                        <span className="text-gray-500">-</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      {customer.subscriptionBillingStart ? (
                        <div>
                          <p className="text-orange-300">{formatDate(customer.subscriptionBillingStart)}</p>
                          <p className="text-xs text-gray-500">{new Date(customer.subscriptionBillingStart) > new Date() ? 'scheduled' : 'applied'}</p>
                        </div>
                      ) : (
                        <span className="text-gray-500">—</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-400">
                      {formatDate(customer.createdAt)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex flex-wrap gap-2">
                        <button
                          onClick={() => openPricingModal(customer)}
                          className="text-blue-400 hover:text-blue-300 text-xs px-2 py-1 bg-blue-900/20 hover:bg-blue-900/40 rounded"
                        >
                          Edit Pricing
                        </button>
                        <button
                          onClick={() => openBillingDateModal(customer)}
                          className="text-orange-400 hover:text-orange-300 text-xs px-2 py-1 bg-orange-900/20 hover:bg-orange-900/40 rounded"
                        >
                          Billing Date
                        </button>
                        {needsStripeSyncAttention(customer) && (
                          <>
                            <button
                              onClick={() => retryStripeSync(customer.id)}
                              disabled={actionLoading === 'retry-stripe-sync'}
                              className="text-purple-400 hover:text-purple-300 text-xs px-2 py-1 bg-purple-900/20 hover:bg-purple-900/40 rounded disabled:opacity-50"
                            >
                              {actionLoading === 'retry-stripe-sync' ? 'Retrying...' : 'Retry Sync'}
                            </button>
                            <button
                              onClick={() => resolveStripeSync(customer.id)}
                              disabled={actionLoading === 'resolve-stripe-sync'}
                              className="text-teal-400 hover:text-teal-300 text-xs px-2 py-1 bg-teal-900/20 hover:bg-teal-900/40 rounded disabled:opacity-50"
                            >
                              {actionLoading === 'resolve-stripe-sync' ? 'Resolving...' : 'Mark Resolved'}
                            </button>
                          </>
                        )}
                        <button
                          onClick={() => openCustomerDetails(customer)}
                          className="text-green-400 hover:text-green-300 text-xs px-2 py-1 bg-green-900/20 hover:bg-green-900/40 rounded"
                        >
                          View Details
                        </button>
                        <button
                          onClick={() => resetPassword(customer.id)}
                          disabled={actionLoading === 'reset-password'}
                          className="text-yellow-400 hover:text-yellow-300 text-xs px-2 py-1 bg-yellow-900/20 hover:bg-yellow-900/40 rounded disabled:opacity-50"
                        >
                          {actionLoading === 'reset-password' ? 'Resetting...' : 'Reset Password'}
                        </button>
                        <button
                          onClick={() => {
                            const amount = prompt('Enter credit amount (USD):');
                            const reason = prompt('Enter reason for credit:');
                            if (amount && !isNaN(Number(amount))) {
                              applyCredit(customer.id, Number(amount), reason || 'Admin credit');
                            }
                          }}
                          disabled={actionLoading === 'apply-credit'}
                          className="text-cyan-300 hover:text-cyan-200 text-xs px-2 py-1 bg-cyan-900/20 hover:bg-cyan-900/40 rounded disabled:opacity-50"
                        >
                          {actionLoading === 'apply-credit' ? 'Applying...' : 'Apply Credit'}
                        </button>
                        {customer.subscriptionStatus === 'active' ? (
                          <button
                            onClick={() => {
                              const reason = prompt('Enter reason for pausing subscription:');
                              pauseSubscription(customer.id, reason || 'Admin action');
                            }}
                            disabled={actionLoading === 'pause-subscription'}
                            className="text-red-400 hover:text-red-300 text-xs px-2 py-1 bg-red-900/20 hover:bg-red-900/40 rounded disabled:opacity-50"
                          >
                            {actionLoading === 'pause-subscription' ? 'Pausing...' : 'Pause Sub'}
                          </button>
                        ) : (
                          <button
                            onClick={() => resumeSubscription(customer.id)}
                            disabled={actionLoading === 'resume-subscription'}
                            className="text-green-400 hover:text-green-300 text-xs px-2 py-1 bg-green-900/20 hover:bg-green-900/40 rounded disabled:opacity-50"
                          >
                            {actionLoading === 'resume-subscription' ? 'Resuming...' : 'Resume Sub'}
                          </button>
                        )}
                        <button
                          onClick={() => deleteCustomer(customer.id, customer.email)}
                          disabled={actionLoading === 'delete-customer'}
                          className="text-red-500 hover:text-red-400 text-xs px-2 py-1 bg-red-900/30 hover:bg-red-900/50 rounded disabled:opacity-50 font-semibold"
                        >
                          {actionLoading === 'delete-customer' ? 'Deleting...' : 'Delete'}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {filteredCustomers.length === 0 && !loading && (
          <div className="text-center py-12">
            <p className="text-gray-400">No customers found</p>
          </div>
        )}
      </div>

      {/* Pricing Modal */}
      {showPricingModal && selectedCustomer && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-gray-800 rounded-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-bold text-white mb-4">
              Edit Subscription for {selectedCustomer.username}
            </h2>
            
            <div className="space-y-4">
              {/* Plan Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Subscription Plan
                </label>
                <select
                  value={selectedPlan}
                  onChange={(e) => setSelectedPlan(e.target.value)}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="">Select a plan</option>
                  {availablePlans.map((plan) => (
                    <option key={plan.id} value={plan.name}>
                      {plan.name} - {formatCurrency(plan.basePrice)} base
                    </option>
                  ))}
                </select>
              </div>

              {/* Custom Price */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Custom Monthly Price (in dollars, 0 for free)
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={customPrice / 100}
                  onChange={(e) => setCustomPrice(Math.round(parseFloat(e.target.value) * 100))}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <p className="mt-1 text-xs text-gray-400">
                  Enter 0 for free access. Leave at base price or customize.
                </p>
              </div>
            </div>

            <div className="mt-6 flex gap-3">
              <button
                onClick={updateCustomerSubscription}
                className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 transition-colors"
              >
                Update Subscription
              </button>
              <button
                onClick={closePricingModal}
                className="flex-1 bg-gray-600 text-white py-2 px-4 rounded-md hover:bg-gray-500 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Billing Date Modal */}
      {showBillingDateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-gray-800 rounded-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-bold text-white mb-1">Set Billing Start Date</h2>
            <p className="text-sm text-gray-400 mb-5">
              Choose the date when Stripe should first charge this customer. A future date creates a free trial period until that day. Today or a past date starts billing immediately.
            </p>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Billing Start Date
                </label>
                <input
                  type="date"
                  value={billingDateInput}
                  onChange={(e) => setBillingDateInput(e.target.value)}
                  className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md text-white focus:outline-none focus:ring-2 focus:ring-orange-500"
                />
              </div>

              {billingDateInput && (
                <div className={`p-3 rounded-lg text-sm ${new Date(billingDateInput) > new Date() ? 'bg-orange-900/20 border border-orange-500/30 text-orange-300' : 'bg-green-900/20 border border-green-500/30 text-green-300'}`}>
                  {new Date(billingDateInput) > new Date()
                    ? `Customer will not be charged until ${new Date(billingDateInput).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}.`
                    : 'Billing will start immediately — Stripe will end any active trial now.'}
                </div>
              )}
            </div>

            <div className="mt-6 flex gap-3">
              <button
                onClick={saveBillingDate}
                disabled={!billingDateInput || actionLoading === 'set-billing-date'}
                className="flex-1 bg-orange-600 text-white py-2 px-4 rounded-md hover:bg-orange-700 transition-colors disabled:opacity-50"
              >
                {actionLoading === 'set-billing-date' ? 'Saving...' : 'Save Billing Date'}
              </button>
              <button
                onClick={closeBillingDateModal}
                className="flex-1 bg-gray-600 text-white py-2 px-4 rounded-md hover:bg-gray-500 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Customer Details Modal */}
      {showCustomerDetails && selectedCustomer && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-gray-800 rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold text-white mb-4">
              Customer Details
            </h2>
            
            <div className="space-y-4">
              {/* Basic Info */}
              <div className="bg-gray-700 rounded-lg p-4">
                <h3 className="text-sm font-semibold text-gray-400 mb-2">Basic Information</h3>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <p className="text-gray-400">Name</p>
                    <p className="text-white">{selectedCustomer.name}</p>
                  </div>
                  <div>
                    <p className="text-gray-400">Username</p>
                    <p className="text-white">{selectedCustomer.username}</p>
                  </div>
                  <div>
                    <p className="text-gray-400">Email</p>
                    <p className="text-white">{selectedCustomer.email}</p>
                  </div>
                  <div>
                    <p className="text-gray-400">Role</p>
                    <p className="text-white capitalize">{selectedCustomer.role?.toLowerCase()}</p>
                  </div>
                  {selectedCustomer.companyName && (
                    <div>
                      <p className="text-gray-400">Company</p>
                      <p className="text-white">{selectedCustomer.companyName}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Subscription Info */}
              {selectedCustomer.subscription && (
                <div className="bg-gray-700 rounded-lg p-4">
                  <h3 className="text-sm font-semibold text-gray-400 mb-2">Subscription</h3>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <p className="text-gray-400">Plan</p>
                      <p className="text-white">{selectedCustomer.subscription.planName}</p>
                    </div>
                    <div>
                      <p className="text-gray-400">Price</p>
                      <p className={`font-semibold ${selectedCustomer.subscription.price === 0 ? 'text-blue-400' : 'text-green-400'}`}>
                        {selectedCustomer.subscription.price === 0 ? 'No Charge' : formatCurrency(selectedCustomer.subscription.price)}
                      </p>
                    </div>
                    <div>
                      <p className="text-gray-400">Status</p>
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(selectedCustomer.subscription.status)}`}>
                        {selectedCustomer.subscription.status}
                      </span>
                    </div>
                    <div>
                      <p className="text-gray-400">Billing Start Date</p>
                      {selectedCustomer.subscriptionBillingStart ? (
                        <div>
                          <p className="text-orange-300 font-medium">{formatDate(selectedCustomer.subscriptionBillingStart)}</p>
                          <p className="text-xs text-gray-500">
                            {new Date(selectedCustomer.subscriptionBillingStart) > new Date() ? 'Future — not yet charged' : 'Date applied'}
                          </p>
                        </div>
                      ) : (
                        <p className="text-gray-500">Not set</p>
                      )}
                    </div>
                    {selectedCustomer.subscription.stripeSubscriptionId && (
                      <div>
                        <p className="text-gray-400">Stripe Sub ID</p>
                        <p className="text-white text-xs font-mono">{selectedCustomer.subscription.stripeSubscriptionId}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Property Group Info */}
              {selectedCustomer.propertyGroup && (
                <div className="bg-gray-700 rounded-lg p-4">
                  <h3 className="text-sm font-semibold text-gray-400 mb-2">Property Group</h3>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <p className="text-gray-400">Name</p>
                      <p className="text-white">{selectedCustomer.propertyGroup.name}</p>
                    </div>
                    <div>
                      <p className="text-gray-400">Properties</p>
                      <p className="text-white">{selectedCustomer.propertyGroup.propertyCount}</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Account Info */}
              <div className="bg-gray-700 rounded-lg p-4">
                <h3 className="text-sm font-semibold text-gray-400 mb-2">Account</h3>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <p className="text-gray-400">Joined</p>
                    <p className="text-white">{formatDate(selectedCustomer.createdAt)}</p>
                  </div>
                  <div>
                    <p className="text-gray-400">Last Updated</p>
                    <p className="text-white">{formatDate(selectedCustomer.updatedAt)}</p>
                  </div>
                  {selectedCustomer.stripeCustomerId && (
                    <div className="col-span-2">
                      <p className="text-gray-400">Stripe Customer ID</p>
                      <p className="text-white text-xs font-mono">{selectedCustomer.stripeCustomerId}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="mt-6">
              <button
                onClick={closeCustomerDetails}
                className="w-full bg-gray-600 text-white py-2 px-4 rounded-md hover:bg-gray-500 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomerManagement;
